import React, { useState } from "react";
import { Box, Paper, Typography, CircularProgress, Alert } from "@mui/material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import MonthlyStatistics from "./components/MonthlyStatistics";
import MonthYearSelector from "./components/MonthYearSelector";
import DetailModal from "./components/DetailModal";
import { useCalendarData } from "./hooks/useCalendarData";
import { useDayDetails } from "./hooks/useDayDetails";
import { formatDateLocal } from "./utils/dateUtils";
import {
  renderDayCellContent,
  getDayCellClassNames,
  calendarStyles,
} from "./utils/calendarHelpers";

const DeliveryReport = ({ driverId, driverName }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [clickedDate, setClickedDate] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Fetch calendar data
  const { monthData, summaryData, loading, error } = useCalendarData(
    driverId,
    year,
    month
  );

  // Fetch day details
  const {
    selectedDay,
    loading: dayDetailsLoading,
    fetchDayDetails,
    clearSelectedDay,
  } = useDayDetails();

  // Handle year change
  const handleYearChange = (event) => {
    const newYear = event.target.value;
    const newDate = new Date(newYear, month, 1);
    setCurrentDate(newDate);
  };

  // Handle month change
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    const newDate = new Date(year, newMonth, 1);
    setCurrentDate(newDate);
  };

  // Custom day cell content using didMount
  const dayCellDidMount = (arg) => {
    const dayData = monthData.find((d) => d.date === formatDateLocal(arg.date));
    renderDayCellContent(arg, dayData);
  };

  // Handle date click
  const handleDateClick = (arg) => {
    // Prevent multiple clicks while loading
    if (dayDetailsLoading || modalOpen) return;

    const dayData = monthData.find((d) => d.date === arg.dateStr);
    if (dayData && dayData.status === "completed") {
      // Open modal immediately and show loading state
      setClickedDate(dayData);
      setModalOpen(true);
      // Fetch detailed day data from API
      fetchDayDetails(driverId, arg.dateStr);
    }
    // Rest days are not clickable
  };

  // Day cell class names
  const dayCellClassNames = (arg) => {
    const dayData = monthData.find((d) => d.date === formatDateLocal(arg.date));
    return getDayCellClassNames(dayData);
  };

  // Handle modal close
  const handleModalClose = () => {
    clearSelectedDay();
    setClickedDate(null);
    setModalOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Typography variant="body1" color="textSecondary">
            Monthly delivery statistics
          </Typography>
        </Box>

        {/* Monthly Statistics */}
        <MonthlyStatistics summaryData={summaryData} />

        {/* Year and Month Selector */}
        <MonthYearSelector
          year={year}
          month={month}
          onYearChange={handleYearChange}
          onMonthChange={handleMonthChange}
        />

        {/* FullCalendar */}
        <Box sx={calendarStyles}>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={currentDate}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "",
            }}
            dateClick={handleDateClick}
            dayCellDidMount={dayCellDidMount}
            dayCellClassNames={dayCellClassNames}
            height="auto"
            fixedWeekCount={false}
            showNonCurrentDates={false}
            datesSet={(dateInfo) => {
              // Update current date when month changes
              const newDate = new Date(
                dateInfo.start.getFullYear(),
                dateInfo.start.getMonth(),
                1
              );
              setCurrentDate(newDate);
            }}
          />
        </Box>
      </Paper>

      {/* Detail Modal */}
      <DetailModal
        day={selectedDay || clickedDate}
        onClose={handleModalClose}
        loading={dayDetailsLoading}
        open={modalOpen}
        driverName={driverName}
      />
    </Box>
  );
};

export default DeliveryReport;
